import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useP } from '../../i18n/useP';
import Card from '../../components/Card';
import { ReportNavContext } from '../../components/reportNav';
import { getForecastAssumptions, putForecastAssumption } from '../../api/forecastAssumptions';
import { getVatPlan, VAT_PERIOD_LBL } from '../../api/vatPlan';
import '../ForecastCashPlan.css';

/* Section TVA (Finance) — MÊME structure que la vue TVA du plan de liquidité,
   mais sur l'ANNÉE ENTIÈRE, avec la frontière réel / prévisionnel marquée
   (demande Olivier 2026-08-17).

   Le décompte est CALCULÉ PAR LE SERVEUR (moteur migré du JavaScript le
   2026-08-21) : cette page LIT la vue (`/financials/vat-plan`) — courus,
   échéancier, versements effectifs, solde dû — et le bilan consomme la même
   série en live. Ici ne restent que les SAISIES (versements, remboursements),
   partagées avec le plan de liquidité : hypothèse `vat_plan` du client × année. */

const MONTHS_ABBR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

const fmt = (v, unit) => {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Math.round(Number(v) / (unit === 'chf' ? 1 : 1000));
  return n === 0 ? '–' : n.toLocaleString('de-CH');
};

export default function VatYearPage({ activeTenant, year, unit = 'kchf', closeAction = null }) {
  const p = useP();
  const nav = useContext(ReportNavContext);
  const tid = activeTenant?.id;
  const [vp, setVp] = useState(null);        // vue serveur du décompte
  const [assum, setAssum] = useState({});

  const load = useCallback(() => {
    if (!tid) { setVp(null); return; }
    getVatPlan(tid, year).then((r) => setVp(r.error ? null : r.data)).catch(() => setVp(null));
    getForecastAssumptions(tid, year).then((r) => setAssum(r.error ? {} : (r.data || {}))).catch(() => setAssum({}));
  }, [tid, year]);
  useEffect(() => { load(); }, [load]);

  const cfg = (assum.vat_plan && typeof assum.vat_plan === 'object') ? assum.vat_plan : {};
  const payments = (cfg.payments && typeof cfg.payments === 'object') ? cfg.payments : {};
  const refunds = (cfg.refunds && typeof cfg.refunds === 'object') ? cfg.refunds : {};
  const setCfg = (patch) => {
    const next = { payments, refunds, ...patch };
    setAssum((a) => ({ ...a, vat_plan: next }));
    if (tid) {
      putForecastAssumption(tid, year, 'vat_plan', { payments: next.payments, refunds: next.refunds })
        .then(() => load()).catch(() => {});
    }
  };

  if (!tid) return <p className="fcp-loading">{p("Sélectionne d'abord une entreprise.")}</p>;
  if (!vp) return <p className="fcp-loading">{p('Chargement…')}</p>;

  const closed = Number(vp.closed) || 0;
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const payEff = (m) => Number(vp.payments_effective?.[m - 1]) || 0;
  const refundAt = (m) => Number(vp.refunds?.[m - 1]) || 0;
  const balAt = (m) => (m > closed ? vp.balance[m - 1] : null);
  const openAt = (m) => (m > closed ? (m === 1 ? 0 : vp.balance[m - 2]) : null);
  const isOverridden = (m) => Boolean(vp.overridden?.[m - 1]);
  const recalcPayments = () => {
    const next = {};
    for (let m = closed + 1; m <= 12; m += 1) next[String(m)] = vp.computed_payments[m - 1] || 0;
    setCfg({ payments: next });
  };
  const clearPayments = () => setCfg({ payments: {} });

  const edge = (m) => (m === closed + 1 && closed > 0 && closed < 12 ? ' fcp-edge' : '');
  const cell = (v, m) => (
    <td key={m} className={`fcp-num${Number(v) < 0 ? ' is-neg' : ''}${edge(m)}`}>
      {v == null ? '–' : fmt(v, unit)}
    </td>
  );
  /* AUTONOME (sans Finance, 2026-09-28) : le solde dû à l'ouverture se TAPE
     dans le premier mois ouvert ; vide, le logiciel l'estime. */
  const saveOpening = (chf) => {
    const oi = (assum.opening_items && typeof assum.opening_items === 'object') ? assum.opening_items : {};
    const it = { ...(oi.vat || {}) };
    if (chf == null) delete it.amount; else it.amount = chf;
    putForecastAssumption(tid, year, 'opening_items', { ...oi, vat: it }).then(() => load()).catch(() => {});
  };
  const row = (label, at, cls = '', sg = 1, editAt = null) => (
    <tr className={cls}>
      <td className="fcp-lbl">{label}</td>
      {months.map((m) => {
        if (editAt && m === closed + 1) {
          return (
            <td key={m} className={`fcp-num fcp-editcell${edge(m)}`}>
              <VatPayInput chf={vp.opening_due} unit={unit} empty onCommit={editAt} />
            </td>
          );
        }
        const v = at(m); return cell(v == null ? null : sg * v, m);
      })}
      <td className="fcp-num fcp-total">
        {fmt(months.reduce((a, m) => a + sg * (Number(at(m)) || 0), 0), unit)}
      </td>
    </tr>
  );
  const nOpen = 12 - closed;

  return (
    <Card
      className="report-card"
      title={p('Décompte TVA')}
      subtitle={`${p('réel + prévisionnel')} · ${year} · ${unit === 'chf' ? 'CHF' : 'kCHF'}`}
      actions={closeAction}
    >
      {/* Réglages LUS (non saisis ici) : information au-dessus du tableau, une
          ligne par réglage, lien en dessous — tout aligné à gauche
          (retour Olivier 2026-08-17). */}
      <div className="fcp-info">
        <div className="fcp-info__l">
          <i className="fcp-info__ic" aria-hidden>ⓘ</i>
          {p('décompte')} <b>{VAT_PERIOD_LBL[vp.period] || vp.period}</b>
        </div>
        <div className="fcp-info__l">
          {p('paiement')} <b>{vp.lag ? `${vp.lag * 30} ${p('jours')}` : p('à la clôture')}</b>
        </div>
        {/* Le taux et la périodicité se règlent dans les PARAMÈTRES du client,
            section Finance, onglet TVA (2026-09-27) ; « Fermer et revenir »
            du panneau ramène ici. L'ancienne adresse de Finance n'existe plus :
            elle retombait sur l'Accueil. */}
        <button type="button" className="fcp-info__link"
          onClick={() => {
            try { window.localStorage.setItem('fin_settings_tab', JSON.stringify('vat')); } catch (e) { /* quota */ }
            if (nav?.goSettings) nav.goSettings('finance');
          }}>
          {p('Paramètres › Finance › TVA')} →
        </button>
      </div>

      <div className="fcp-wrap">
        {/* Format des autres détails (demande d'Olivier, 2026-09-27) : les soldes
            d'ouverture et de fin se lisent en gras sur un fond clair, plus sur
            l'aplat noir du plan, trop fort pour une table de détail. */}
        <table className={`fcp fcp--soft${unit === 'chf' ? ' fcp--chf' : ''}`}>
          <thead>
            {/* Bande de régime : réel (mois clos) | prévisionnel (mois ouverts). */}
            <tr className="fcp-bandrow">
              <th className="fcp-lbl" />
              {closed > 0 && <th className="fcp-band fcp-band--real" colSpan={closed}>{p('Réalisé')}</th>}
              {nOpen > 0 && <th className="fcp-band fcp-band--fore" colSpan={nOpen}>{p('Prévisionnel')}</th>}
              <th className="fcp-num fcp-total" />
            </tr>
            <tr>
              <th className="fcp-lbl" />
              {months.map((m) => <th key={m} className={`fcp-num${edge(m)}`}>{MONTHS_ABBR[m - 1]}</th>)}
              <th className="fcp-num fcp-total">{p('Total')}</th>
            </tr>
          </thead>
          <tbody>
            {/* Le solde dû des COMPTES ouvre le décompte (2026-09-28) ; ce que le
                logiciel aurait estimé se lit à côté, en repère seulement. */}
            {row(<>{p('Solde dû à l’ouverture')}
              {/* Rien de tapé (autonome) : le décompte part de l'estimation, et le dit. */}
              {vp.opening_estimate != null && (vp.opening_due == null
                || Math.abs(Number(vp.opening_estimate) - Number(vp.opening_due)) >= 0.5) && (
                <span className="fcp-est"> · {p('estimé')} {fmt(vp.opening_estimate, unit)}</span>
              )}</>, openAt, 'fcp-open', 1, vp.opening_editable ? saveOpening : null)}
            {row(p('TVA collectée sur ventes'), (m) => vp.billed[m - 1], 'fcp-pos')}
            {row(p('TVA payée sur achats'), (m) => vp.ded_purchases[m - 1], 'fcp-neg', -1)}
            {row(p('TVA payée sur charges'), (m) => vp.ded_opex[m - 1], 'fcp-neg', -1)}
            {row(p('TVA nette de la période'), (m) => vp.net[m - 1], 'fcp-sub')}
            <tr className="fcp-neg">
              <td className="fcp-lbl">
                {p("Paiement à l'AFC")}
                <button type="button" className="fcp-recalc" onClick={recalcPayments}
                  title={p('Recalculer les versements selon la périodicité et le délai des Paramètres')}>↻</button>
                {Object.keys(payments).length > 0 && (
                  <button type="button" className="fcp-recalc" onClick={clearPayments}
                    title={p('Revenir aux versements calculés')}>×</button>
                )}
              </td>
              {/* Mois clos : pas de saisie, pas d'affichage — le passé vit dans
                  la banque, pas dans le décompte (pur forward, 2026-08-20). */}
              {months.map((m) => (m <= closed
                ? <td key={m} className={`fcp-num${edge(m)}`}>–</td>
                : (
                  <td key={m} className={`fcp-num${isOverridden(m) ? ' is-edited' : ''}${edge(m)}`}>
                    <VatPayInput chf={-payEff(m)} unit={unit}
                      onCommit={(chf) => setCfg({ payments: { ...payments, [String(m)]: Math.abs(chf) } })} />
                  </td>
                )))}
              <td className="fcp-num fcp-total">{fmt(-months.reduce((a, m) => a + (m > closed ? payEff(m) : 0), 0), unit)}</td>
            </tr>
            <tr className="fcp-pos">
              <td className="fcp-lbl">{p('Remboursement reçu de l’AFC')}</td>
              {months.map((m) => (m <= closed
                ? <td key={m} className={`fcp-num${edge(m)}`}>–</td>
                : (
                  <td key={m} className={`fcp-num${refundAt(m) ? ' is-edited' : ''}${edge(m)}`}>
                    <VatPayInput chf={refundAt(m)} unit={unit}
                      onCommit={(chf) => setCfg({ refunds: { ...refunds, [String(m)]: Math.abs(chf) } })} />
                  </td>
                )))}
              <td className="fcp-num fcp-total">{fmt(months.reduce((a, m) => a + refundAt(m), 0), unit)}</td>
            </tr>
            {row(p('Solde dû fin de mois'), balAt, 'fcp-close')}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* Versement d'un mois — même grammaire que dans le plan de liquidité. */
function VatPayInput({ chf, unit, onCommit, empty = false }) {
  const [draft, setDraft] = useState(null);
  const div = unit === 'chf' ? 1 : 1000;
  const shown = draft != null ? draft : (Math.abs(Number(chf) || 0) < 0.5 ? '' : String(Math.round(Number(chf) / div)));
  const commit = () => {
    if (draft == null) return;
    const v = parseFloat(String(draft).replace(/['\s’]/g, '').replace(',', '.'));
    setDraft(null);
    // `empty` : un champ vidé s'efface (null) au lieu de valoir zéro.
    if (empty && String(draft).trim() === '') { onCommit(null); return; }
    onCommit(Number.isFinite(v) ? v * div : 0);
  };
  return (
    <input className="fcp-paycell fcp-field" value={shown} placeholder="–"
      onChange={(e) => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setDraft(null); }} />
  );
}
